import { TPerformer, TSlot } from 'types/Orders/distribution';

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { THeader, THistoryRecord, TReason, TRow, TWorkflowState } from '../types';

const initialState: TWorkflowState = {
    headerData: [],
    data: [],
    history: [],
    historyIndex: 0,
    selectedCell: null,
    editableWork: null,
    selectedRow: null,
    reasons: {},
};

const workflowSlice = createSlice({
    name: 'workflow',
    initialState,
    reducers: {
        setWorkflow(state, action: PayloadAction<{ headerData: THeader; data: TRow[] }>) {
            const { headerData, data } = action.payload;
            state.headerData = headerData;
            state.data = data;
        },

        delEmptyCol(state, action: PayloadAction<number>) {
            const colIndex = action.payload;
            state.headerData = state.headerData.filter((_, idx) => idx !== colIndex);
            const performerId = state.headerData[colIndex]?.id;
            state.data = state.data.map((row) => {
                const updatedAssigned = { ...row.assigned };
                if (performerId !== undefined) {
                    updatedAssigned[performerId] = null;
                }
                return {
                    ...row,
                    assigned: updatedAssigned,
                };
            });
        },

        delEmptyRow(state, action: PayloadAction<number>) {
            const rowIndex = action.payload;
            state.data = state.data.filter((_, idx) => idx !== rowIndex);
        },

        selectCell(state, action: PayloadAction<{ row: number; col: number; slot: TSlot } | null>) {
            state.selectedCell = action.payload;
        },

        selectRow(state, action: PayloadAction<number | null>) {
            state.selectedRow = action.payload;
        },
        mergeRows(state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) {
            const { fromIndex, toIndex } = action.payload;
            if (fromIndex === toIndex) return;

            const fromRow = state.data[fromIndex];
            const toRow = state.data[toIndex];
            if (!fromRow || !toRow) return;

            // 1) Переносим все задачи из fromRow → toRow.unassigned
            const tasksToMove: TSlot[] = [
                ...(Object.values(fromRow.assigned).filter(Boolean) as TSlot[]),
                ...fromRow.unassigned,
            ];

            let maxPosition = toRow.unassigned.length
                ? Math.max(...toRow.unassigned.map((t) => t.position))
                : -1;

            for (const task of tasksToMove) {
                maxPosition++;
                toRow.unassigned.push({ ...task, position: maxPosition });
            }

            // 2) Удаляем строку-источник
            state.data.splice(fromIndex, 1);

            // 3) Все строки ниже fromIndex сдвигают row.queue на -1
            for (let i = fromIndex; i < state.data.length; i++) {
                state.data[i].queue = `${+state.data[i].queue - 1}`;
            }
        },

        updateData(state, action: PayloadAction<TRow[]>) {
            state.data = action.payload;
        },
        updateHeader(state, action: PayloadAction<THeader>) {
            state.headerData = action.payload;
        },

        setReasons(state, action: PayloadAction<{ performerId: number; reasons: TReason[] }>) {
            const { performerId, reasons } = action.payload;
            state.reasons[performerId] = reasons;
        },

        clearReasons(state, action: PayloadAction<number>) {
            delete state.reasons[action.payload];
        },

        attachPerformer(
            state,
            action: PayloadAction<{
                newPerformer: TPerformer;
                oldPerformerId: number;
            }>
        ) {
            const { newPerformer, oldPerformerId } = action.payload;
            const position = -oldPerformerId - 1;

            if (!state.headerData.some((p) => p.id === newPerformer.id)) {
                state.headerData.push(newPerformer);
            }

            state.data.forEach((row) => {
                const task = row.unassigned.find((item) => item.position === position);
                if (task) {
                    row.assigned[newPerformer.id] = task;
                } else {
                    row.assigned[newPerformer.id] = null;
                }
                row.unassigned = row.unassigned
                    .map((item) =>
                        item.position !== position
                            ? { ...item, position: item.position - +(item.position > position) }
                            : null
                    )
                    .filter(Boolean) as typeof row.unassigned;
            });
        },

        changePerformer(
            state,
            action: PayloadAction<{
                oldPerformerId: number;
                newPerformer: TPerformer;
            }>
        ) {
            const { oldPerformerId, newPerformer } = action.payload;

            state.headerData = state.headerData.filter((item) => item.id !== oldPerformerId);

            if (!state.headerData.some((p) => p.id === newPerformer.id)) {
                state.headerData.push(newPerformer);
            }

            state.data.forEach((row) => {
                if (row.assigned[oldPerformerId]) {
                    const task = row.assigned[oldPerformerId];
                    delete row.assigned[oldPerformerId];

                    row.assigned[newPerformer.id] = task;
                }
            });
        },

        detachPerformer(state, action: PayloadAction<{ performerId: number }>) {
            const { performerId } = action.payload;

            state.headerData = state.headerData.filter((p) => p.id !== performerId);

            state.data.forEach((row) => {
                if (row.assigned[performerId]) {
                    const task = {
                        ...row.assigned[performerId],
                        position: 0,
                    };
                    row.unassigned = row.unassigned
                        .map((item) => ({
                            ...item,
                            position: item.position + 1,
                        }))
                        .concat([task]);
                }
                delete row.assigned[performerId];
            });
        },

        setEditableWork(state, action: PayloadAction<any>) {
            const work = action.payload;
            state.editableWork = work;
        },

        addHistoryStep: (state) => {
            const newEntry: THistoryRecord = {
                data: JSON.parse(JSON.stringify(state.data)),
                headerData: JSON.parse(JSON.stringify(state.headerData)),
            };

            state.history = state.history.slice(0, state.historyIndex + 1);

            state.history.push(newEntry);
            state.historyIndex = state.history.length - 1;
        },

        undo: (state) => {
            if (state.historyIndex > 0) {
                state.historyIndex -= 1;
                const prev = state.history[state.historyIndex];
                state.data = JSON.parse(JSON.stringify(prev.data));
                state.headerData = JSON.parse(JSON.stringify(prev.headerData));
            }
        },

        redo: (state) => {
            if (state.historyIndex < state.history.length - 1) {
                state.historyIndex += 1;
                const next = state.history[state.historyIndex];
                state.data = JSON.parse(JSON.stringify(next.data));
                state.headerData = JSON.parse(JSON.stringify(next.headerData));
            }
        },
        clearHistory: (state) => {
            state.history = [];
        },
    },
});

export const {
    setWorkflow,
    delEmptyCol,
    delEmptyRow,
    selectCell,
    selectRow,
    mergeRows,
    updateData,
    updateHeader,
    setReasons,
    clearReasons,
    attachPerformer,
    changePerformer,
    detachPerformer,
    setEditableWork,
    addHistoryStep,
    undo,
    redo,
    clearHistory,
} = workflowSlice.actions;

export default workflowSlice.reducer;
